import { useState, useEffect, useCallback, useMemo } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import InternalBookingCardBase from '../common/InternalBookingCardBase';
import useInternalBooking from '../../../../hooks/useInternalBooking';
import { extractUpgradeSymbols } from '../../../../util/upgradeSymbolsHelper';

const colorCalc = id => {
  if (id === 0) return 'orange';
  if (id === 1) return 'green';
  return '#ffffff';
};

// Stable empty array to avoid creating a new reference on every render
const EMPTY_UPGRADES = [];

function BookingCardLasertagv2({
  booking,
  updateBookingState,
  slotMode,
  handleOpenModal,
  renderAvailableSlots,
  onBookingShift,
  entertainment,
  onDragStart,
  onDragActivated,
  onDragEnd,
  availableShiftTimes,
  isShiftable,
}) {
  const { updateInternalBooking } = useInternalBooking();
  const [teamColor, setTeamColor] = useState('#ffffff');
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const { _id, ltSpecific, participantCount } = booking;
  const playerGroupID = ltSpecific?.playerGroupID;

  const upgradeSymbols = useMemo(() => extractUpgradeSymbols(booking), [booking]);

  // Only re-run when the actual playerGroupID value changes, not the object reference
  useEffect(() => {
    if (playerGroupID !== undefined) {
      setTeamColor(colorCalc(playerGroupID));
    }
  }, [playerGroupID]);

  const handleGreaterThanClick = useCallback(e => {
    e.stopPropagation();
    e.preventDefault();
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const updatePlayerGroupID = useCallback(
    async newId => {
      const update = { ltSpecific: { playerGroupID: newId } };
      const result = await updateInternalBooking(_id, update);
      setTeamColor(colorCalc(result.ltSpecific.playerGroupID));
      updateBookingState(_id, result);
      handleClose();
    },
    [_id, updateInternalBooking, updateBookingState, handleClose]
  );

  const handleCardClick = useCallback(
    (bookingId, color) => handleOpenModal(bookingId, color),
    [handleOpenModal]
  );

  const shouldShowAttentionStripesExtra = useCallback(
    ({ booking: bk, presentSince, finishedAt }) =>
      Boolean(presentSince && !finishedAt && !bk?.ltSpecific?.briefedAt),
    []
  );

  const renderExtraLeftStatusIcons = useCallback(
    ({ booking: bk, presentSince, finishedAt, isEffectivelyUnpaid }) => {
      const isPresentPaidNoBriefing =
        presentSince && !finishedAt && !isEffectivelyUnpaid && !bk?.ltSpecific?.briefedAt;

      if (!isPresentPaidNoBriefing) return null;

      return (
        <RecordVoiceOverIcon
          sx={{
            color: 'info.main',
            verticalAlign: 'middle',
            mr: 1.5,
            width: 18,
            height: 18,
          }}
        />
      );
    },
    []
  );

  const renderAfterName = useCallback(({ booking: bk }) => {
    const gt = bk?.ltSpecific?.groupType;
    if (!gt) return null;
    return (
      <Box
        component='span'
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 16,
          height: 16,
          borderRadius: '3px',
          fontSize: '10px',
          fontWeight: 'bold',
          lineHeight: 1,
          color: '#fff',
          backgroundColor: gt === 'kids' ? 'orange' : 'green',
          ml: 0.5,
          verticalAlign: 'text-bottom',
        }}
      >
        {gt === 'kids' ? 'K' : 'E'}
      </Box>
    );
  }, []);

  // Stable topOffset functions – only change when slotMode changes
  const topOffsetFn = useMemo(
    () => (slotMode ? bk => 64 + (bk.i - 1) * 45 : bk => 40 + (bk.i - 1) * 45),
    [slotMode]
  );

  const getParticipantCount = useCallback(bk => bk.participantCount, []);

  // Stable iconProps object – only reconstructed when its values actually change
  const iconProps = useMemo(
    () => ({
      upgrades: upgradeSymbols,
      manualUpgrades: EMPTY_UPGRADES,
      participantCount,
      slotMode,
      onGreaterThanClick: handleGreaterThanClick,
      color: teamColor,
    }),
    [upgradeSymbols, participantCount, slotMode, handleGreaterThanClick, teamColor]
  );

  const handleOrangeClick = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();
      updatePlayerGroupID(0);
    },
    [updatePlayerGroupID]
  );

  const handleGreenClick = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();
      updatePlayerGroupID(1);
    },
    [updatePlayerGroupID]
  );

  return (
    <>
      <InternalBookingCardBase
        booking={booking}
        color={teamColor}
        onCardClick={handleCardClick}
        onBookingShift={onBookingShift}
        entertainment={entertainment}
        shouldShowAttentionStripesExtra={shouldShowAttentionStripesExtra}
        renderExtraLeftStatusIcons={renderExtraLeftStatusIcons}
        renderAfterName={renderAfterName}
        onDragStart={onDragStart}
        onDragActivated={onDragActivated}
        onDragEnd={onDragEnd}
        availableShiftTimes={availableShiftTimes}
        isShiftable={isShiftable}
        renderAvailableSlots={renderAvailableSlots}
        topOffset={topOffsetFn}
        getParticipantCount={getParticipantCount}
        iconProps={iconProps}
      >
        {slotMode && (
          <Menu
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleClose}
            MenuListProps={{ 'aria-labelledby': 'basic-button' }}
            data-ignore-card-click
          >
            <MenuItem
              sx={{ color: '#e88935', fontWeight: 'bold' }}
              data-ignore-card-click
              onClick={handleOrangeClick}
            >
              Orange
            </MenuItem>
            <MenuItem
              sx={{ color: '#32a852', fontWeight: 'bold' }}
              data-ignore-card-click
              onClick={handleGreenClick}
            >
              Grün
            </MenuItem>
          </Menu>
        )}
      </InternalBookingCardBase>
    </>
  );
}

export default BookingCardLasertagv2;
